import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { userPool, STORAGE_KEYS, TOKEN_EXPIRY, getErrorMessage, COGNITO_CONFIG } from '../config/cognito';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.sessionCheckInterval = null;
    this.refreshPromise = null;
    this.setupSessionMonitoring();
  }



  // Setup automatic session monitoring
  setupSessionMonitoring() {
    // Check session every 5 minutes
    this.sessionCheckInterval = setInterval(() => {
      this.checkAndRefreshSession();
    }, 5 * 60 * 1000);
  }

  // Cleanup on service destruction
  destroy() {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
  }

  // Sign Up
  async signUp(username, email, password) {
    return new Promise((resolve, reject) => {
      const attributes = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: email,
        }),
      ];

      userPool.signUp(username, password, attributes, null, (err, result) => {
        if (err) {
          reject({
            code: err.code,
            message: getErrorMessage(err),
            originalError: err,
          });
        } else {
          resolve({
            user: result.user,
            userSub: result.userSub,
            message: 'User registered successfully. Please verify your email.',
          });
        }
      });
    });
  }

  // Confirm Sign Up
  async confirmSignUp(username, confirmationCode) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
        if (err) {
          reject({
            code: err.code,
            message: getErrorMessage(err),
            originalError: err,
          });
        } else {
          resolve({
            message: 'User confirmed successfully. You can now sign in.',
            result,
          });
        }
      });
    });
  }

  // Sign In
  async signIn(username, password) {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async (session) => {
          await this.handleSuccessfulAuth(session, cognitoUser);
          resolve({
            session,
            user: cognitoUser,
            message: 'Sign in successful',
          });
        },
        onFailure: (err) => {
          reject({
            code: err.code,
            message: getErrorMessage(err),
            originalError: err,
          });
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          reject({
            code: 'NEW_PASSWORD_REQUIRED',
            message: 'Password change required',
            userAttributes,
            requiredAttributes,
          });
        },
      });
    });
  }

  // Handle successful authentication
  async handleSuccessfulAuth(session, user) {
    this.currentUser = user;

    // Store tokens securely
    this.storeTokens(session);

    // Store user attributes and wait for completion
    await this.storeUserAttributes(user);

    // Also extract and store email from ID token for immediate access
    const idToken = session.getIdToken();
    if (idToken) {
      const idTokenPayload = this.parseJWTPayload(idToken.getJwtToken());
      if (idTokenPayload.email || idTokenPayload['cognito:username']) {
        const currentAttributes = this.getUserAttributes() || {};
        const updatedAttributes = {
          ...currentAttributes,
          email: idTokenPayload.email || idTokenPayload['cognito:username'],
          sub: idTokenPayload.sub,
          preferred_username: idTokenPayload['cognito:username'] || idTokenPayload.preferred_username,
        };
        localStorage.setItem(STORAGE_KEYS.USER_ATTRIBUTES, JSON.stringify(updatedAttributes));
      }
    }

    // Set session expiry
    const accessToken = session.getAccessToken().getJwtToken();
    const accessTokenPayload = this.parseJWTPayload(accessToken);
    let expiryTime = null;

    if (accessTokenPayload.exp) {
      // exp is in seconds, convert to milliseconds
      expiryTime = accessTokenPayload.exp * 1000;
    }

    this.setSessionExpiry(expiryTime);
  }

  // Store tokens in localStorage
  storeTokens(session) {
    if (session.isValid()) {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, session.getAccessToken().getJwtToken());
      localStorage.setItem(STORAGE_KEYS.ID_TOKEN, session.getIdToken().getJwtToken());
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, session.getRefreshToken().getToken());
    }
  }

  // Store user attributes (returns Promise)
  storeUserAttributes(user) {
    return new Promise((resolve) => {
      user.getUserAttributes((err, attributes) => {
        if (!err && attributes) {
          const userData = {};
          attributes.forEach(attr => {
            userData[attr.getName()] = attr.getValue();
          });
          localStorage.setItem(STORAGE_KEYS.USER_ATTRIBUTES, JSON.stringify(userData));
          resolve(userData);
        } else {
          if (err) {
            console.error('Error getting user attributes:', err);
          }
          // Resolve anyway to not block auth flow, but log the issue
          resolve(null);
        }
      });
    });
  }

  // Set session expiry
  setSessionExpiry(expiryTime = null) {
    let expiry;
    if (expiryTime) {
      expiry = expiryTime;
    } else {
      // Fallback to static configuration if no expiry provided
      expiry = Date.now() + TOKEN_EXPIRY.ACCESS_TOKEN;
    }
    localStorage.setItem(STORAGE_KEYS.SESSION_EXPIRY, expiry.toString());
  }

  // Sign Out
  // Sign Out
  signOut() {
    console.trace('authService: signOut called');
    this.clearStoredData();

    if (this.currentUser) {
      this.currentUser.signOut();
      this.currentUser = null;
    }

    return Promise.resolve({ message: 'Sign out successful' });
  }

  // Global Sign Out (invalidates all sessions)
  async globalSignOut() {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        this.clearStoredData();
        resolve({ message: 'Global sign out successful' });
        return;
      }

      this.currentUser.globalSignOut({
        onSuccess: (result) => {
          this.clearStoredData();
          this.currentUser = null;
          resolve({ message: 'Global sign out successful', result });
        },
        onFailure: (err) => {
          // Even if global sign out fails on server, clear local data
          this.clearStoredData();
          this.currentUser = null;
          reject({
            code: err.code,
            message: getErrorMessage(err),
            originalError: err,
          });
        },
      });
    });
  }

  // Clear stored data
  clearStoredData() {
    // Clear our custom keys
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });

    // Clear AWS Cognito SDK keys
    // These start with 'CognitoIdentityServiceProvider'
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('CognitoIdentityServiceProvider')) {
        localStorage.removeItem(key);
      }
    });

    // Clear permissions key
    localStorage.removeItem("user-permissions");

    sessionStorage.clear();
  }

  // Get current session
  async getSession() {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        reject({ message: 'No authenticated user' });
        return;
      }

      this.currentUser.getSession((err, session) => {
        if (err) {
          reject({
            code: err.code,
            message: getErrorMessage(err),
            originalError: err,
          });
        } else if (session.isValid()) {
          resolve(session);
        } else {
          reject({ message: 'Invalid session' });
        }
      });
    });
  }

  // Check if user is authenticated
  isAuthenticated() {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const sessionExpiry = localStorage.getItem(STORAGE_KEYS.SESSION_EXPIRY);

    if (!accessToken || !sessionExpiry) {
      return false;
    }

    const now = Date.now();
    const expiry = parseInt(sessionExpiry);

    return now < expiry;
  }

  // Get stored tokens
  getStoredTokens() {
    return {
      accessToken: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
      idToken: localStorage.getItem(STORAGE_KEYS.ID_TOKEN),
      refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
    };
  }

  // Get user attributes
  getUserAttributes() {
    const stored = localStorage.getItem(STORAGE_KEYS.USER_ATTRIBUTES);
    return stored ? JSON.parse(stored) : null;
  }

  // Refresh session
  async refreshSession() {
    if (this.refreshPromise) {
      console.log('refreshSession: usage of existing promise');
      return this.refreshPromise;
    }

    this.refreshPromise = new Promise((resolve, reject) => {
      try {
        console.log('refreshSession: Starting refresh attempt...');
        const refreshTokenString = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

        if (!refreshTokenString) {
          console.error('refreshSession: No refresh token found in localStorage at key:', STORAGE_KEYS.REFRESH_TOKEN);
          this.refreshPromise = null;
          reject({ message: 'No refresh token available' });
          return;
        }

        if (!this.currentUser) {
          console.log('refreshSession: this.currentUser is null, attempting to reconstruct...');
          // Try to recreate user from stored data
          let userAttributes = this.getUserAttributes();
          let username = userAttributes?.sub;

          if (!username) {
            // Fallback: Try to get username from ID Token
            console.log('refreshSession: userAttributes missing sub, trying ID string...');
            const idToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
            if (idToken) {
              const payload = this.parseJWTPayload(idToken);
              username = payload.sub;
              console.log('refreshSession: Recovered username from ID token:', username);
            }
          }

          if (username) {
            console.log('refreshSession: Reconstructing CognitoUser with sub:', username);
            this.currentUser = new CognitoUser({
              Username: username,
              Pool: userPool,
            });
          } else {
            console.error('refreshSession: Cannot identify user. userAttributes and ID token fallback failed.');
            this.refreshPromise = null;
            reject({ message: 'Cannot identify user' });
            return;
          }
        } else {
          console.log('refreshSession: currentUser already exists:', this.currentUser.getUsername());
        }

        const refreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenString });

        console.log('refreshSession: invoking SDK refreshSession...');
        this.currentUser.refreshSession(refreshToken, async (err, session) => {
          // Clear the promise so future calls can start new refreshes
          this.refreshPromise = null;

          if (err) {
            console.error('refreshSession: SDK callback returned error:', err);
            reject({
              code: err.code,
              message: getErrorMessage(err),
              originalError: err,
            });
          } else {
            console.log('refreshSession: SDK callback successful');
            await this.handleSuccessfulAuth(session, this.currentUser);
            resolve({
              session,
              message: 'Session refreshed successfully',
            });
          }
        });
      } catch (executorError) {
        console.error('refreshSession: Synchronous error in executor:', executorError);
        this.refreshPromise = null;
        reject(executorError);
      }
    });

    return this.refreshPromise;
  }

  // Check and refresh session if needed
  async checkAndRefreshSession() {
    const isAuthed = this.isAuthenticated();
    const sessionExpiry = localStorage.getItem(STORAGE_KEYS.SESSION_EXPIRY);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const now = Date.now();

    // If we have a refresh token but are not authenticated (expired) or about to expire
    if (refreshToken) {
      // If not authenticated (likely expired), try to refresh immediately
      if (!isAuthed) {
        console.log('Session expired but refresh token exists. Attempting refresh...');
        try {
          await this.refreshSession();
          return true;
        } catch (error) {
          console.error('Error refreshing expired session:', error);
          this.signOut();
          return false;
        }
      }

      // If authenticated but expiring soon (less than 5 minutes)
      const expiry = parseInt(sessionExpiry);
      if (expiry - now < 5 * 60 * 1000) {
        console.log('Session expiring soon. Attempting refresh...');
        try {
          await this.refreshSession();
          return true;
        } catch (error) {
          console.error('Error refreshing session:', error);
          // Don't sign out yet if we still have some valid time, 
          // but if we're very close to expiry it might be safer.
          // For now, we'll let the next check handle it or natural expiry
          return false;
        }
      }

      return true;
    }

    // No refresh token and not authenticated
    if (!isAuthed) {
      return false;
    }

    return true;
  }

  // Forgot Password
  async forgotPassword(username) {
    return new Promise((resolve, reject) => {
      if (!username || !username.trim()) {
        reject({
          code: 'InvalidParameterException',
          message: 'Username or email is required',
        });
        return;
      }

      const cognitoUser = new CognitoUser({
        Username: username.trim(),
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          console.log('Forgot password success:', data);
          resolve({
            message: 'A verification code has been sent to your email',
            data: data,
          });
        },
        onFailure: (err) => {
          console.error('Forgot password failure:', err);
          reject({
            code: err.code || 'UnknownError',
            message: getErrorMessage(err),
            originalError: err,
          });
        },
      });
    });
  }

  // Confirm Forgot Password
  async confirmForgotPassword(username, confirmationCode, newPassword) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(confirmationCode, newPassword, {
        onSuccess: () => {
          resolve({
            message: 'Password changed successfully',
          });
        },
        onFailure: (err) => {
          reject({
            code: err.code,
            message: getErrorMessage(err),
            originalError: err,
          });
        },
      });
    });
  }

  // Change Password (for authenticated users)
  async changePassword(oldPassword, newPassword) {
    if (!this.currentUser) {
      await this.refreshSession();
    }

    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        reject({ message: 'User not authenticated' });
        return;
      }
      this.currentUser.getSession((sessionErr, session) => {
        if (sessionErr || !session?.isValid()) {
          reject({ message: 'User not authenticated' });
          return;
        }
        this.currentUser.changePassword(oldPassword, newPassword, (err, result) => {
          if (err) {
            reject({
              code: err.code,
              message: getErrorMessage(err),
              originalError: err,
            });
          } else {
            resolve({
              message: 'Password changed successfully',
              result,
            });
          }
        });
      });
    });
  }

  // Resend confirmation code
  async resendConfirmationCode(username) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject({
            code: err.code,
            message: getErrorMessage(err),
            originalError: err,
          });
        } else {
          resolve({
            message: 'Confirmation code resent',
            result,
          });
        }
      });
    });
  }

  // Update user attributes
  async updateUserAttributes(attributes) {
    return new Promise((resolve, reject) => {
      if (!this.currentUser) {
        reject({ message: 'User not authenticated' });
        return;
      }

      const attributeList = Object.entries(attributes).map(([key, value]) => {
        return new CognitoUserAttribute({
          Name: key,
          Value: value,
        });
      });

      this.currentUser.updateAttributes(attributeList, (err, result) => {
        if (err) {
          reject({
            code: err.code,
            message: getErrorMessage(err),
            originalError: err,
          });
        } else {
          // Update local user attributes
          this.updateLocalUserAttributes(attributes);
          resolve({
            message: 'User attributes updated successfully',
            result,
          });
        }
      });
    });
  }

  // Update local user attributes
  updateLocalUserAttributes(attributes) {
    if (this.currentUser) {
      const currentAttributes = this.getUserAttributes();
      const updatedAttributes = { ...currentAttributes, ...attributes };

      // Store updated attributes in localStorage
      localStorage.setItem(STORAGE_KEYS.USER_ATTRIBUTES, JSON.stringify(updatedAttributes));
    }
  }

  // OAuth Sign In with Google
  signInWithGoogle() {
    if (!COGNITO_CONFIG.Domain) {
      throw new Error('Cognito domain is not configured for OAuth');
    }

    const domain = COGNITO_CONFIG.Domain;
    const clientId = COGNITO_CONFIG.ClientId;
    const redirectUri = `${window.location.origin}/app`;

    const oauthUrl = `https://${domain}/oauth2/authorize?` +
      `identity_provider=Google&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `scope=email%20openid%20profile`;

    // Redirect to Google OAuth
    window.location.href = oauthUrl;
  }

  // Sign Up with Google (same as sign in for OAuth)
  signUpWithGoogle() {
    return this.signInWithGoogle();
  }

  // Handle OAuth callback (call this when user returns from OAuth)
  async handleOAuthCallback(code) {
    if (!code) {
      throw new Error('No authorization code received from OAuth provider');
    }

    try {
      // Exchange code for tokens
      const domain = COGNITO_CONFIG.Domain;
      const clientId = COGNITO_CONFIG.ClientId;
      const redirectUri = `${window.location.origin}/app`;

      const tokenUrl = `https://${domain}/oauth2/token`;

      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code: code,
        redirect_uri: redirectUri,
      });

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });

      if (!response.ok) {
        throw new Error('Failed to exchange authorization code for tokens');
      }

      const tokens = await response.json();

      // Store tokens
      if (tokens.access_token) {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
      }
      if (tokens.id_token) {
        localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.id_token);
      }
      if (tokens.refresh_token) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
      }

      // Set session expiry
      const expiryTime = Date.now() + ((tokens.expires_in || 3600) * 1000);
      localStorage.setItem(STORAGE_KEYS.SESSION_EXPIRY, expiryTime.toString());

      // Get user attributes from ID token
      if (tokens.id_token) {
        const userAttributes = this.parseJWTPayload(tokens.id_token);
        localStorage.setItem(STORAGE_KEYS.USER_ATTRIBUTES, JSON.stringify(userAttributes));
      }

      return {
        message: 'OAuth sign in successful',
        tokens,
      };
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw {
        code: 'OAuthError',
        message: error.message || 'Failed to complete OAuth sign in',
        originalError: error,
      };
    }
  }

  // Helper to parse JWT payload
  parseJWTPayload(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return {};
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
